/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { maestrosBdCorpoServices, maestrosPNPServices, maestrosTramiteServices } from '@/services/maestros.service';

import { ESTADOS_FIRMA } from '@/constants/tramite.constants';
import { type ProcessedData } from '@/interfaces/model/fetch.interface';
import { type ILookup } from '@/interfaces/model/generic.interface';
import { type IMaestrosGeneric, type ImaestrosTramite } from '@/interfaces/model/maestros.interface';
import { makeObjEmptyArray } from '@/utils/object/object';

// Importamos el store de usuario para leer el token
import { useSessionStore } from './session.store';

const maestrosGenericServices = { ...maestrosPNPServices, ...maestrosBdCorpoServices };

type MaestrosState = {
	// Variables
	maestrosGeneric: IMaestrosGeneric;
	maestrosTramite: ImaestrosTramite;

	// Flags
	isGenericLoaded: boolean;
	isTramiteLoaded: boolean;
	tipoTramiteIdLoaded: number;

	// Acciones
	fetchMaestrosGeneric: () => Promise<void>;
	fetchmaestrosTramite: (tipoTramiteId: number) => Promise<void>;
	clean: () => void;
	cleanMaestroTramite: () => void;
};

export const maestrosTramiteDefault: ImaestrosTramite = {
	canalNotificacion: [],
	estadoSubsanacion: [],
	estadoTramite: [],
	estadoRevisionFirma: [],
	tipoComunicacionEmisor: [],
	tipoComunicacionPlaton: [],
	tipoDocumentoIdent: [],
	tipoDocumento: [],
	tipoEvento: [],
	tipoIntervencion: [],
	tipoOrigen: [],
	tipoPersona: [],
	tipoSubsanacion: [],
	configuracionTramite: undefined,
};

// Helper interno para cargar servicios
const loadServiceMap = async (
	services: Record<string, () => Promise<ProcessedData<any>>>,
	onSuccess: (data: any) => void,
) => {
	try {
		const keys = Object.keys(services);
		const requests = Object.values(services).map((fn) => fn());
		const responses = await Promise.all(requests);

		if (responses.every((res) => res.code === 200)) {
			const mapped = keys.reduce(
				(acc, key, index) => ({
					...acc,
					[key]: responses[index]?.data,
				}),
				{},
			);
			onSuccess(mapped);
		} else {
			console.warn('Algunos servicios no devolvieron 200', responses);
		}
	} catch (error) {
		console.error('Error cargando maestros:', error);
	}
};

const mapEstadosFirma = (estados: ILookup[]): ILookup[] => 
	estados
		.filter((it) => it.id !== ESTADOS_FIRMA.CANCELADA)
		.map((estado) => {
			if (estado.id === ESTADOS_FIRMA.RECHAZADA) {
				estado.descripcion = 'Rechazada'; 
			}
			return estado;
		});

export const useMaestrosStore = create(
	persist<MaestrosState>(
		(set, get) => ({
			// Estado Inicial
			maestrosGeneric: makeObjEmptyArray(Object.keys(maestrosGenericServices)),
			maestrosTramite: maestrosTramiteDefault,
			isGenericLoaded: false,
			isTramiteLoaded: false,
			tipoTramiteIdLoaded: 0,

			async fetchMaestrosGeneric() {
				const state = get();

				// ACCESO CROSS-STORE:
				// Leemos el token directamente del estado actual del userStore
				const userToken = useSessionStore.getState().user?.token;

				// Si ya cargó o no hay token, no hacemos nada
				if (state.isGenericLoaded || !userToken) return;

				await loadServiceMap(maestrosGenericServices, (data) => {
					set({ maestrosGeneric: data, isGenericLoaded: true });
				});
			},

			async fetchmaestrosTramite(tipoTramiteId: number) {
				set({ isTramiteLoaded: false });

				// Nota: Asumo que estos servicios también requieren autenticación.
				// Si la necesitan, verifica el token aquí también como en el método anterior.

				await loadServiceMap(maestrosTramiteServices, (data) => {
					set({ maestrosTramite: {...data, estadoRevisionFirma: mapEstadosFirma(data.estadoSubsanacion)}, isTramiteLoaded: true, tipoTramiteIdLoaded: tipoTramiteId });
				});
			},

			cleanMaestroTramite() {
				set({
					isTramiteLoaded: false,
					maestrosTramite: maestrosTramiteDefault,
				});
			},

			clean() {
				set({
					isGenericLoaded: false,
					isTramiteLoaded: false,
					maestrosGeneric: makeObjEmptyArray(Object.keys(maestrosGenericServices)),
					maestrosTramite: maestrosTramiteDefault,
				});
				sessionStorage.removeItem('maestros-storage');
			},
		}),
		{
			name: 'maestros-storage', // Nombre único para este store
			storage: createJSONStorage(() => sessionStorage),
			partialize: (state) =>
				({
					maestrosGeneric: state.maestrosGeneric,
					maestrosTramite: state.maestrosTramite,
					isGenericLoaded: state.isGenericLoaded,
					isTramiteLoaded: state.isTramiteLoaded,
				}) as MaestrosState,
		},
	),
);
